import '../App.css';
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import background from "../assets/Background.jpg";
import hiclLogo from "../assets/hicl-logo.png";
import trigLogo from "../assets/trig-logo-new.png";

const GEO_CACHE_KEY = "hicl_geo_status_v1";

// ---------------- CONFIG ----------------
const EVENT_TITLE = "Investor Presentation";
const EVENT_DATETIME = "Nov 17, 2025 10:00:00"; // <- change for new event
const DATE_FORMATTED = "Monday, 17th November 2025, 10:00 AM (GMT)";
const WEBCAST_URL = "https://stream.brrmedia.co.uk/broadcast/68f6124464cab60013721242";

const RESTRICTED_ROUTE = "/restricted";

// const BLOCKED_COUNTRY_CODES = ["US"];
const BLOCKED_COUNTRY_CODES = null;
// const ALLOWED_COUNTRY_CODES = null;
const ALLOWED_COUNTRY_CODES = ["GB", "IE", "SE", "NL", "JE", "GG", "IM"];

const GEO_API_URL = "https://ipapi.co/json/";
// ----------------------------------------

function useCountdown(targetDateString) {
    const [timeLeft, setTimeLeft] = useState(null);
    const [isPast, setIsPast] = useState(false);

    useEffect(() => {
        const target = new Date(targetDateString).getTime();

        const tick = () => {
            const now = Date.now();
            const distance = target - now;

            if (distance <= 0) {
                setIsPast(true);
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const dayMs = 1000 * 60 * 60 * 24;
            const hourMs = 1000 * 60 * 60;
            const minuteMs = 1000 * 60;
            const secondMs = 1000;

            const days = Math.floor(distance / dayMs);
            const hours = Math.floor((distance % dayMs) / hourMs);
            const minutes = Math.floor((distance % hourMs) / minuteMs);
            const seconds = Math.floor((distance % minuteMs) / secondMs);

            setTimeLeft({ days, hours, minutes, seconds });
        };

        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [targetDateString]);

    return { timeLeft, isPast };
};

function LandingPage() {
    const [geoLoading, setGeoLoading] = useState(true);
    const [geoError, setGeoError] = useState(null);
    const [countryCode, setCountryCode] = useState(null);
    const [isBlocked, setIsBlocked] = useState(false);
    const [hasScrolledDisclaimer, setHasScrolledDisclaimer] = useState(false);

    const isAgreeDisabled = geoLoading || !!geoError || isBlocked || !hasScrolledDisclaimer;

    const { timeLeft, isPast } = useCountdown(EVENT_DATETIME);
    const navigate = useNavigate();

    useEffect(() => {
        let cancelled = false;

        async function checkGeo() {
            try {
                const cached = localStorage.getItem(GEO_CACHE_KEY);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    if (cancelled) return;

                    const { countryCode: cachedCode, isBlocked: cachedBlocked, geoError: cachedError } = parsed;

                    setCountryCode(cachedCode || null);
                    setIsBlocked(!!cachedBlocked);
                    setGeoError(cachedError || null);
                    setGeoLoading(false);

                    if (cachedBlocked) {
                        navigate(RESTRICTED_ROUTE, { replace: true });
                    }

                    return;
                }

                const res = await fetch(GEO_API_URL);
                if (!res.ok) throw new Error("Failed to fetch geo data");
                const data = await res.json();

                if (cancelled) return;

                const code = data.country || data.country_code || null;

                let blocked = false;

                if (ALLOWED_COUNTRY_CODES && ALLOWED_COUNTRY_CODES.length > 0) {
                    blocked = !ALLOWED_COUNTRY_CODES.includes(code);
                } else if (BLOCKED_COUNTRY_CODES && BLOCKED_COUNTRY_CODES.length > 0) {
                    blocked = BLOCKED_COUNTRY_CODES.includes(code);
                }

                setCountryCode(code);
                setIsBlocked(blocked);
                setGeoError(null);
                setGeoLoading(false);

                const payload = {
                    countryCode: code,
                    isBlocked: blocked,
                    geoError: null,
                    timestamp: Date.now(),
                };
                try {
                    localStorage.setItem(GEO_CACHE_KEY, JSON.stringify(payload));
                } catch {

                }

                if (blocked) {
                    navigate(RESTRICTED_ROUTE, { replace: true });
                }
            } catch (err) {
                console.error("Geo error:", err);
                if (!cancelled) {
                    setGeoError("Unable to verify your location.");
                    setGeoLoading(false);
                }
            }
        }

        checkGeo();
        return () => {
            cancelled = true;
        };
    }, [navigate]);

    const renderTimer = () => {
        if (!timeLeft) return "Calculating...";
        if (isPast) return "Event now in progress";

        const { days, hours, minutes, seconds } = timeLeft;

        const parts = [];
        if (days > 0) parts.push(`${days} Day${days > 1 ? "s" : ""}`);
        parts.push(`${hours} Hours`);
        parts.push(`${minutes} Minutes`);
        parts.push(`${seconds} Seconds`);

        return parts.join(" ");
    };

    const handleDisclaimerScroll = (e) => {
        const el = e.currentTarget;
        const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 10;
        if (atBottom) {
            setHasScrolledDisclaimer(true);
        }
    };

    const handleAgree = () => {
        // Add any extra tracking / logging here
        window.location.href = WEBCAST_URL;
    };

    const handleDisagree = () => {
        // window.location.href = RESTRICTED_ROUTE;
        navigate(RESTRICTED_ROUTE);
    };

    return (
        <div className="page-root" style={{
            background: `url(${background}) center center / cover no-repeat fixed`,
        }}>
            <div className="overlay-card">
                <div className="logo-box">
                <img
                    src={hiclLogo}
                    alt="Event Logo"
                    className="event-logo"
                />
                <img
                    src={trigLogo}
                    alt="Event Logo"
                    className="event-logo"
                />
                </div>
                <h3 className="title">
                    {EVENT_TITLE}
                </h3>
                <h3 className="title">
                    {DATE_FORMATTED}
                </h3>
                <h3 className="timer" id="timer">
                    {renderTimer()}
                </h3>

                <div className="divider" />

                {/* <h3>Disclaimer:</h3> */}
                {/* <h3 className="disclaimer-heading">INVESTOR PRESENTATION</h3> */}
                {/* <p>By proceeding to the webcast, you represent, warrant and agree that:</p> */}
                <p className="disclaimer-intro">
                    <b>IMPORTANT: You must read the following disclaimer before continuing. </b>
                    For the purposes of this disclaimer, references to "the presentation"
                    (or other equivalent references) shall mean and include materials,
                    including and together with any oral commentary or presentation and
                    any question and answer session. You are advised to read this
                    disclaimer carefully before clicking on the <b>AGREE</b> button below and
                    accessing and viewing the Presentation.  By viewing or accessing the
                    presentation, whether live or recorded, you will be deemed to have
                    agreed to the following restrictions and acknowledged that you
                    understand the legal and regulatory sanctions attached to the misuse,
                    disclosure or improper circulation of the presentation or any
                    information contained herein. The presentation may not be reproduced
                    (in whole or in part), distributed or transmitted to any other person
                    without the prior written consent of InfraRed Capital Partners Limited
                    ("InfraRed").
                </p>

                {!hasScrolledDisclaimer && !isBlocked && (
                    <p className="scroll-hint">
                        Please scroll through the full disclaimer to enable the <b>Agree</b> button.
                    </p>
                )}

                <div className="disclaimer-scroll" onScroll={handleDisclaimerScroll}>
                    <span><b>
                        THE PRESENTATION IS NOT FOR PUBLICATION, RELEASE, DISCLOSURE OR
                        DISTRIBUTION, AND WILL NOT OTHERWISE BE MADE AVAILABLE, IN WHOLE OR
                        IN PART, DIRECTLY OR INDIRECTLY, IN OR INTO THE UNITED STATES OR TO
                        ANY RESIDENT THEREOF OR ANY OTHER JURISDICTION OR TO ANY OTHER
                        PERSON WHERE SUCH DISTRIBUTION IS UNLAWFUL. THE PRESENTATION MAY
                        NOT BE TAKEN OR TRANSMITTED INTO, AND MAY NOT BE COPIED, FORWARDED,
                        OR DISTRIBUTED, IN WHOLE OR IN PART, IN OR INTO THE UNITED STATES OR
                        TO ANY RESIDENT THEREOF OR TO, OR FOR THE ACCOUNT OR BENEFIT OF
                        "U.S. PERSONS" AS DEFINED IN REGULATION S UNDER THE US SECURITIES
                        ACT OF 1933, AS AMENDED (THE "US SECURITIES ACT") OR INTO
                        AUSTRALIA, CANADA, JAPAN, SOUTH AFRICA, ANY MEMBER STATE OF THE EEA
                        (SAVE FOR NETHERLANDS, SWEDEN AND THE REPUBLIC OF IRELAND IN RESPECT
                        OF PROFESSIONAL INVESTORS ONLY), OR INTO ANY JURISDICTION WHERE ITS
                        RELEASE, PUBLICATION OR DISTRIBUTION IS OR MAY BE UNLAWFUL OR ANY
                        OTHER JURISDICTION OR TO ANY OTHER PERSON WHERE TO DO SO WOULD BE
                        UNLAWFUL (EACH AN “EXCLUDED TERRITORY). THE DISTRIBUTION OF THE
                        PRESENTATION IN OTHER JURISDICTIONS MAY BE RESTRICTED BY LAW, AND
                        PERSONS INTO WHOSE POSSESSION THE PRESENTATION COMES SHOULD INFORM
                        THEMSELVES ABOUT, AND OBSERVE, ANY SUCH RESTRICTIONS. FAILURE TO
                        COMPLY WITH THESE RESTRICTIONS MAY RESULT IN A VIOLATION OF THE US
                        SECURITIES ACT OR THE APPLICABLE LAWS OF OTHER JURISDICTIONS.
                    </b>
                    </span>

                    <p>
                        <b>
                            The contents of the presentation are not a financial promotion and
                            none of the contents of the presentation constitute an invitation or
                            inducement to engage in investment activity. If and to the extent
                            that the presentation or any of its contents are deemed to be a
                            financial promotion, the Company is relying on the exemption
                            provided by Article 69 of the Financial Services and Markets Act
                            2000 (Financial Promotions) Order 2005/1529 in respect of section 21
                            of the Financial Services and Markets Act 2000
                        </b>
                    </p>

                    <p>
                        The presentation has been prepared at the direction of the Company
                        by InfraRed, as investment manager of HICL Infrastructure plc (the
                        "Company"). The presentation has been provided solely for
                        informational purposes only in relation to the proposed transaction
                        contemplated herein (the "Transaction") and the ordinary shares of
                        the Company (the "Shares") which may be issued in connection
                        therewith, and does not purport to be comprehensive or contain all
                        the information that may be required by recipients to evaluate the
                        Company and/or the Shares. The presentation does not constitute or
                        form part of, and should not be construed as, an offer, invitation
                        or inducement to purchase or subscribe for any Shares or any
                        securities of the Company whatsoever or of any other entity, nor
                        shall the presentation or any part of it, or the fact of its
                        existence, form the basis of, or be relied upon in connection with,
                        any contract, investment decision or commitment whatsoever, nor does
                        it constitute a recommendation regarding the securities (including
                        the Shares) of the Company, The Renewables Infrastructure Group
                        Limited ("TRIG") or any other company. The information contained in
                        the presentation has not been independently verified or audited.
                        References to the "Company" in this disclaimer should also be read
                        as references to the Company following the Transaction, should the
                        transaction complete.
                    </p>

                    <p>
                        The presentation should not be considered as a recommendation that
                        any investor should subscribe for or purchase any Shares (or any
                        other securities of the Company or those of TRIG), and nothing in
                        the presentation should be construed as legal, tax, regulatory,
                        accounting or investment advice. The presentation does not disclose
                        all the risks and other significant issues related to making or
                        holding an investment in the securities of the Company (including
                        the Shares) or of TRIG, nor does it propose to do so. Potential
                        investors should ensure that they fully understand the terms and
                        conditions of the Shares and any applicable risks. Any assessment of
                        the Shares should be made solely on the basis of the information
                        contained in the prospectus published by the Company in relation to
                        the Transaction (as supplemented) ("the Prospectus") and the issue
                        of Shares thereunder and not on the basis of the information
                        provided in the presentation.
                    </p>

                    <p>
                        Prospective investors should read the Prospectus and pay particular
                        attention to the description of certain risk factors that are
                        contained therein. No person has been authorised to make any
                        statement concerning the Company other than as set forth in the
                        Prospectus and any statements made that are not contained therein
                        may not be relied upon. The Prospectus will be available on the
                        Company's website at <a href="www.hicl.com" target='_blank'>www.hicl.com</a>. Approval of the Prospectus by the
                        FCA should not be understood as an endorsement of the shares in the
                        Company.
                    </p>

                    <p>
                        No representation or warranty, express or implied, is or will be
                        made by or on behalf of the Company or TRIG in relation to, and no
                        responsibility is or will be accepted by the Company, TRIG, InfraRed,
                        Investec Bank plc, Goldman Sachs International, BNP Paribas or any
                        of (as applicable) their members, directors, officers, employees or
                        affiliates (collectively, the "Relevant Persons") or any other
                        person as to the accuracy, fairness, completeness, reliability or
                        sufficiency of the information contained in the presentation or for
                        any other statement made or purported to be made by or on behalf of
                        any of them and nothing in the presentation shall be deemed to
                        constitute such a representation or warranty. Nothing in the
                        presentation shall be relied upon as a promise or representation in
                        this respect, whether as to the past or the future. InfraRed is not
                        acting as advisor or fiduciary. Accordingly, you must independently
                        determine, with your own advisors, the appropriateness for you of
                        the securities before investing. You are not entitled to rely the
                        presentation and the Company and InfraRed accept no liability
                        whatsoever for any consequential losses arising from the use of the
                        presentation or reliance on the information contained herein.
                    </p>

                    <p>
                        In particular, but without limiting the generality of the foregoing,
                        there can be no guarantee that the Transaction or any of the
                        arrangements contemplated in the presentation complete, on the terms
                        contemplated herein or at all, and none of the Relevant Persons
                        makes any representation or warranty in this regard. None of the
                        Relevant Persons nor any of their affiliates, agents, directors,
                        partners and employees accepts any responsibility whatsoever for, or
                        any liability for any loss howsoever arising, directly or
                        indirectly, from the presentation or its contents (including the
                        failure of the Transaction to complete), or makes any representation
                        or warranty, express or implied, as to the contents of the
                        presentation or for any other statement made or purported to be made
                        by it or them, or on its behalf, including (without limitation)
                        information regarding the Company, TRIG, the Transaction or the
                        Shares and no reliance should be placed on such information. To the
                        fullest extent permitted by applicable law, the Relevant Persons
                        accordingly disclaim any and all responsibility and/or liability,
                        whether arising in tort, contract or otherwise, which they might
                        otherwise have in respect of the presentation or any such statement.
                    </p>

                    <p>
                        The information and opinions contained in the presentation and any
                        other material discussed in the presentation are provided as at the
                        date of the presentation and are subject to change without notice.
                        Neither the Company or any other Relevant Persons are under any
                        obligation to update, revise or supplement the presentation or any
                        additional information or to remedy any inaccuracies in or omissions
                        from the presentation.
                    </p>

                    <p>
                        Each of Investec Bank plc and Goldman Sachs International (the
                        "Joint Financial Advisers") is acting exclusively for the Company
                        and for no one else in relation to the Transaction. Neither Joint
                        Financial Adviser will regard any other person (whether or not a
                        recipient of the presentation) as its client in relation to the
                        Transaction and the other arrangements referred to in the
                        presentation and will not be responsible to anyone other than the
                        Company for providing the protections afforded to their respective
                        clients or for providing any advice in relation to the Transaction,
                        the contents of the presentation or any transaction or arrangement
                        referred to in the presentation.
                    </p>

                    <p>
                        BNP Paribas is acting as financial adviser exclusively for TRIG and
                        no one else in connection with the matters described in the
                        presentation and will not be responsible to anyone other than TRIG
                        for providing the protections afforded to clients of BNP Paribas or
                        for providing advice in relation to the Transaction, the contents of
                        the presentation or any transaction or arrangement referred to in
                        the presentation.
                    </p>

                    <p>
                        The presentation may contain certain forward-looking statements.
                        These statements typically contain words such as "expects" and
                        "anticipates" and words of similar import. By their nature forward
                        looking statements involve risk and uncertainty because they relate
                        to events and depend on circumstances that will occur in the future.
                    </p>

                    <p>
                        <b>Past performance is not indicative of future results.</b> The
                        presentation includes information regarding the performance data of
                        the Company and certain of the Relevant Persons (including TRIG and
                        InfraRed), which may not necessarily be comprehensive. The past
                        performance of the Company and the other Relevant Persons is not a
                        reliable indicator of, and cannot be relied upon as a guide to, the
                        future performance of the Company (or indeed any of the Relevant
                        Persons), including should the Transaction be implemented.
                    </p>

                    <p>
                        The presentation may include target returns for the Company and
                        TRIG, including NAV total return targets and proposed dividend
                        policies (the "target returns"). The target returns are in all cases
                        targets and are not forecasts or projections. Target returns are
                        based on InfraRed’s beliefs and estimates regarding the returns that
                        may be achievable on future investments that the Company intends to
                        pursue, including assumptions regarding holding periods and exit
                        dates, and amount and cost of leverage, but by their nature they are
                        aspirational only. These target returns are not a guarantee,
                        projection or prediction of performance, which is dependent upon a
                        number of factors within and outside InfraRed’s, the Company's and
                        TRIG's control.
                    </p>

                    <p>
                        While the target returns are believed by the Company, TRIG and
                        InfraRed and (to the extent applicable) other Relevant Persons to be
                        reasonable under current circumstances, prospective investors should
                        bear in mind that assumptions underlying the target returns are
                        hypothetical and by their nature aspirational, and actual results
                        (including as a result of circumstances or actions outside of the
                        Relevant Persons’ control) may be better or worse.
                    </p>

                    <p>
                        Certain numerical figures included in the presentation may have been
                        rounded. Therefore, discrepancies in tables between totals and the
                        sums of the amounts listed may occur due to such rounding.
                    </p>

                    <p>
                        The distribution of the presentation and the offer of the Shares in
                        certain jurisdictions may be restricted by law. The presentation is
                        not directed or intended for distribution to, or use by, any person
                        or entity that is a citizen or resident located in any locality,
                        state, country or other jurisdiction where such distribution,
                        publication, availability or use would be contrary to the law or
                        regulation of that jurisdiction or which would require any
                        registration or licensing within such jurisdiction. In particular
                        these materials are not intended for distribution in the United
                        States or to, or for the account or benefit of, US Persons, or into
                        Australia, Canada, Japan, South Africa or any other jurisdiction or
                        to any other person where to do so would be unlawful. Persons who
                        come into possession of any document or other information referred
                        to herein should inform themselves about and observe any such
                        restrictions. Any failure to comply with these restrictions may
                        constitute a violation of the securities laws of such jurisdictions.
                    </p>

                    <p>
                        The Shares have not been, and will not be, registered under the US
                        Securities Act, or under the securities laws or with any securities
                        regulatory authority of any state or other jurisdiction of the
                        United States. The Shares may not be offered, sold, resold,
                        transferred or delivered, directly or indirectly, in or into the
                        United States or to, or for the account or benefit of, US Persons,
                        except pursuant to an exemption from the registration requirements
                        of the US Securities Act and in compliance with any applicable
                        securities laws of any state or other jurisdiction of the United
                        States. The Company has not been, and will not be, registered under
                        the US Investment Company Act of 1940, as amended (the "US
                        Investment Company Act"), and investors will not be entitled to the
                        benefits of that act. No offer, purchase, sale or transfer of the
                        Shares may be made except under circumstances which will not result
                        in the Company being required to register as an investment company
                        under the US Investment Company Act.
                    </p>

                    <p>
                        The presentation has not been approved by the UK Financial Conduct
                        Authority, the US Securities and Exchange Commission, any State
                        securities commission in the United States or any other US
                        regulatory authority, or other relevant regulatory body. None of the
                        information contained herein has been filed with the U.S. Securities
                        and Exchange Commission, any securities administrator under any
                        state securities laws or any other governmental or self-regulatory
                        authority. No governmental authority has passed on the merits of the
                        offering of interests in the Company or the adequacy of the
                        information contained herein. Any representation to the contrary is
                        unlawful.
                    </p>

                    <p>
                        By proceeding and clicking on the “Agree” button, you agree to
                        comply with the terms set out above and confirm that you are a
                        resident of the country you identified earlier and you are accessing
                        this website from within the country you identified earlier, and you
                        additionally represent and warrant to, and agree with InfraRed and
                        the Company that you (i) are not located in the United States; (ii)
                        are not a US Person; (iii) are not investing or otherwise acting for
                        the account or benefit of a US Person or a resident of an Excluded
                        Territory; (iv) are not accessing this website from an Excluded
                        Territory and (v) will not release, forward or distribute
                        information from this website to or for the account or benefit of a
                        US Person or to a person located in any of the Excluded Territories.
                    </p>

                    <p>
                        If you are not permitted to view the website or information
                        contained herein or are in any doubt as to whether you are permitted
                        to view the same, please click the "Disagree" button below or exit
                        this webpage.
                    </p>

                    <p>
                        For the avoidance of doubt, if you are a US Person you must click
                        “Disagree”.
                    </p>
                </div>

                <div className="status">
                    {geoLoading && <p>Checking your location…</p>}
                    {geoError && <p className="error">{geoError}</p>}
                    {!geoLoading && !geoError && (
                        <>
                            <p className="location-info">
                                Detected country:{" "}
                                <strong>{countryCode || "Unknown"}</strong>
                            </p>
                            {isBlocked && (
                                <p className="error">
                                    Access from your location is restricted. Please contact the
                                    organiser if you believe this is an error.
                                </p>
                            )}
                        </>
                    )}
                </div>

                <div className="buttons">
                    <button
                        className="btn btn-success"
                        onClick={handleAgree}
                        disabled={isAgreeDisabled}
                    >
                        {geoLoading
                            ? "Please wait…"
                            : isBlocked
                                ? "Access Restricted"
                                : "Agree"}
                    </button>

                    <button className="btn btn-danger" onClick={handleDisagree}>
                        Disagree
                    </button>
                </div>
            </div>
        </div>
    );
}

export default LandingPage;

